import { useTranslation } from 'react-i18next';

import { useFormatters } from '@/shared/i18n/format';
import type { NotificationItem } from '@/shared/registry/types';

import { ABSENCE_TYPES, type AbsenceType } from '../api';
import { parseIsoDate } from '../lib/people';

const isAbsenceType = (value: string | undefined): value is AbsenceType =>
  !!value && (ABSENCE_TYPES as readonly string[]).includes(value);

/**
 * Text of an `identity.absence-created` notification in the reader's language: who, the kind of absence and the period
 * with localized dates ("Igor Morozov: vacation, 12–18 Dec (7 days)"); to the employee, who recorded it — a colleague
 * (`context=self`) or a business process (`context=process`). Falls back to the server text when the parameters are
 * incomplete.
 */
export function AbsenceNotification({ notification }: { notification: NotificationItem }) {
  const { t } = useTranslation('company');
  const fmt = useFormatters();
  const { params } = notification;
  const type = params.type;
  if (!isAbsenceType(type) || !params.from || !params.to) return <>{notification.fallbackText}</>;
  const from = parseIsoDate(params.from);
  const to = parseIsoDate(params.to);
  const period =
    params.from === params.to
      ? fmt.date(from, 'dateLong')
      : `${fmt.date(from, 'dateShort')} – ${fmt.date(to, from.getFullYear() === to.getFullYear() ? 'dateShort' : 'date')}`;
  const days = Number(params.days) || 1;
  const values = {
    userName: params.userName ?? '',
    actorName: params.actorName ?? '',
    processName: params.processName ?? params.actorName ?? '',
    type: t(`absence.typesLower.${type}`),
    period,
    days: t('absence.days', { count: days }),
  };
  const text =
    params.context === 'self'
      ? t('notifications.absenceSelf', values)
      : params.context === 'process'
        ? t('notifications.absenceProcess', values)
        : t('notifications.absenceManager', values);
  return <>{text}</>;
}
